import React, { useState, useEffect } from 'react';
import { useAuth } from '../../context/AuthContext';
import { getChannels, getMessages, sendMessage } from '../../services/api';
import { PageHeader } from '../../components/Shared';
import { Input } from '../../components/ui/input';
import { Button } from '../../components/ui/button';
import { Avatar, AvatarFallback } from '../../components/ui/avatar';
import { Send, Hash, User as UserIcon } from 'lucide-react';

const Chat = () => {
  const { user } = useAuth();
  const [channels, setChannels] = useState([]);
  const [active, setActive] = useState(null);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');

  useEffect(() => { getChannels().then(c => { setChannels(c); if (c.length) setActive(c[0].id); }); }, []);
  useEffect(() => { if (active) getMessages(active).then(setMessages); }, [active]);

  const send = async (e) => {
    e.preventDefault();
    if (!input.trim() || !active) return;
    const msg = await sendMessage(active, input);
    setMessages([...messages, msg]);
    setInput('');
  };

  const channel = channels.find(c => c.id === active);

  return (
    <div className="p-4 sm:p-6 lg:p-8">
      <PageHeader title="Team Chat" description="Real-time messaging with your team." />
      <div className="bg-white border border-slate-200 rounded-md grid grid-cols-1 md:grid-cols-[260px_1fr] h-[calc(100vh-220px)] overflow-hidden">
        <aside className="border-r border-slate-200 overflow-y-auto bg-slate-50">
          {channels.map(c => (
            <button key={c.id} onClick={() => setActive(c.id)} className={`w-full text-left p-3 flex items-center gap-3 border-b border-slate-100 hover:bg-white transition-colors ${active === c.id ? 'bg-white border-l-4 border-l-blue-700' : ''}`}>
              <div className="h-9 w-9 bg-slate-200 rounded flex items-center justify-center shrink-0">
                {c.type === 'channel' ? <Hash className="h-4 w-4 text-slate-700" /> : <UserIcon className="h-4 w-4 text-slate-700" />}
              </div>
              <div className="flex-1 min-w-0"><div className="text-sm font-medium text-slate-900 truncate">{c.name}</div></div>
            </button>
          ))}
        </aside>
        <section className="flex flex-col">
          <div className="px-5 py-3 border-b border-slate-200 flex items-center gap-2">
            {channel?.type === 'channel' ? <Hash className="h-4 w-4 text-slate-600" /> : <UserIcon className="h-4 w-4 text-slate-600" />}
            <span className="font-semibold text-slate-900">{channel?.name || ''}</span>
          </div>
          <div className="flex-1 overflow-y-auto p-5 space-y-4">
            {messages.map(m => (
              <div key={m.id} className={`flex gap-3 ${m.userId === user.id ? 'flex-row-reverse' : ''}`}>
                <Avatar className="h-8 w-8"><AvatarFallback className="bg-slate-900 text-white text-xs">{m.name.split(' ').map(s => s[0]).join('').slice(0, 2)}</AvatarFallback></Avatar>
                <div className={`max-w-[70%] ${m.userId === user.id ? 'text-right' : ''}`}>
                  <div className="text-xs text-slate-500 mb-1">{m.name} · {m.time}</div>
                  <div className={`inline-block px-3 py-2 rounded-lg text-sm ${m.userId === user.id ? 'bg-slate-900 text-white' : 'bg-slate-100 text-slate-900'}`}>{m.text}</div>
                </div>
              </div>
            ))}
            {messages.length === 0 && <div className="text-center text-slate-500 py-12">No messages yet. Say hi!</div>}
          </div>
          <form onSubmit={send} className="border-t border-slate-200 p-3 flex gap-2">
            <Input value={input} onChange={e => setInput(e.target.value)} placeholder="Type a message..." className="flex-1" />
            <Button type="submit" className="bg-slate-900 hover:bg-slate-800"><Send className="h-4 w-4" /></Button>
          </form>
        </section>
      </div>
    </div>
  );
};

export default Chat;
